import { type Either, left, right } from "fp-ts/es6/Either";
import mapValues from "lodash/mapValues";
import pick from "lodash/pick";
import transform from "lodash/transform";
import uniq from "lodash/uniq";
// biome-ignore lint/style/noRestrictedImports: encapsulation
import { type NeighborsOf, Error as ParseError, Parser, Traversables } from "stackstate-stql";

import type { NodeId } from "~/models/dto";
import { alphabeticalSorter } from "~/tools/alphabetical-sorter";
import { ImmutableMap } from "~/tools/immutable-map";
import { isArray } from "~/tools/is-array";
import { Lazy } from "~/tools/lazy";
import type { Record } from "~/tools/record";

import { allFilterNames } from "../../../app/state/view/query/TopologyQueryFilterSelectors";

const WITH_NEIGHBORS_OF = "withNeighborsOf";

const isNotEmpty = (arrOrString: Array<unknown> | string): boolean => arrOrString.length > 0;

const uniqNotEmpty = <T extends Array<unknown> | string>(arr: Array<T>): Array<T> => uniq(arr.filter(isNotEmpty));

export class TraversalData {
	public static empty: TraversalData = TraversalData.of({});

	/**
	 * Parses the STQL query string and creates `TraversalData` out of the success result.
	 * The resulting `TraversalData` might contain not basic filters, but 2D array filters.
	 *
	 * Use the `TraversalData#containsBasicFilters()` to determine whether or not the filters are basic.
	 */
	public static fromSTQL(query: string, activeFilters: Array<string>): Either<Error, TraversalData> {
		const normalizedQuery = query.trim();
		if (normalizedQuery.length === 0) {
			return right(TraversalData.empty);
		}

		const extractionResult = Traversables.extract(normalizedQuery);
		if (extractionResult == null) {
			return left(new Error("Not Parsable. Invalid syntax"));
		}

		const { basicFilters, componentNames, summary, componentIdentifiers, isExtractable } = extractionResult;

		const traversalData = TraversalData.of({
			filters: activeFilters == null ? basicFilters : pick(basicFilters, activeFilters),
			componentNames,
			componentIdentifiers,
			clausesWithNeighbors: fromNeighborsOfToString(
				(summary[WITH_NEIGHBORS_OF] as Array<NeighborsOf>) ?? [],
				normalizedQuery,
			),
			isExtractable,
		});

		return right(traversalData);
	}

	/**
	 * ! This method changes order of the values in the filters.
	 * ! Use it where the order is not important, e.g. for the comparison.
	 *
	 * A method that normalizes the STQL query string by sorting all of the values in the filters.
	 * It helps to find out of two queries are the same, but with different order of the values.
	 */
	public static normalizeSTQL(query: string): string {
		return TraversalData.fromSTQL(query, allFilterNames)
			.map((traversalData) =>
				traversalData.isExtractable ? traversalData.toSTQL({ sortValues: alphabeticalSorter }) : query,
			)
			.getOrElse(query);
	}

	/**
	 * Creates `TraversalData` from basic filters (single dimension array) shaped as an object.
	 */
	public static fromBasicFilters(filters: Record<string, Array<string>>): TraversalData {
		return TraversalData.of({
			filters: mapValues(filters, (values) => [values]),
		});
	}

	/**
	 * The method is purposely private to avoid spreading of the `TraversalData` creation mechanics.
	 * Methods `TraversalData.fromSTQL` and `TraversalData.fromBasicFilters` cover enough use cases
	 * across the application codebase.
	 *
	 * For tests it might use combination of `TraversalData.empty` with `TraversalData#replace*` methods.
	 */
	private static of({
		filters = {},
		componentNames = [],
		clausesWithNeighbors = [],
		componentIdentifiers = [],
		isExtractable = true,
	}: {
		filters?: Record<string, Array<Array<string>>>;
		componentNames?: Array<string>;
		clausesWithNeighbors?: Array<string>;
		componentIdentifiers?: Array<string>;
		isExtractable?: boolean;
	}): TraversalData {
		const entries = transform(
			filters,
			(acc: Array<[string, Array<Array<string>>]>, groups, key) => {
				const validGroups = uniqNotEmpty(groups.map(uniqNotEmpty));

				if (isNotEmpty(validGroups)) {
					acc.push([key, validGroups]);
				}

				return acc;
			},
			[],
		);

		return new TraversalData(
			ImmutableMap.fromEntries(entries),
			componentNames,
			clausesWithNeighbors,
			componentIdentifiers,
			isExtractable,
		);
	}

	private readonly filters: ImmutableMap<string, Array<Array<string>>>;
	private readonly hasBasicFilters: Lazy<boolean>;
	private readonly isExtractable: boolean;
	public readonly componentNames: Array<string>;
	public readonly clausesWithNeighbors: Array<string>;
	public readonly componentIdentifiers: Array<string>;

	private constructor(
		filters: ImmutableMap<string, Array<Array<string>>>,
		componentNames: Array<string>,
		clausesWithNeighbors: Array<string>,
		componentIdentifiers: Array<string>,
		isExtractable: boolean,
	) {
		this.filters = filters;
		this.componentNames = uniqNotEmpty(componentNames);
		this.clausesWithNeighbors = uniqNotEmpty(clausesWithNeighbors);
		this.componentIdentifiers = uniqNotEmpty(componentIdentifiers);
		this.isExtractable = isExtractable;
		/**
		 * For avoiding potentially heavy calculations for cases it is better to evaluate it lazily.
		 * Applicable for cases when a `TraversalData` instance chains its methods:
		 *
		 * @example
		 * const query = TraversalData.empty
		 *   .replaceComponentNames(["biba"])
		 *   .replaceComponentIdentifiers(["boba"])
		 *   .replaceFiltersFor("label", [["label:a", "label:b"]])
		 *   .replaceFiltersFor("health", [["CRITICAL"]])
		 *   .toSTQL()
		 */
		this.hasBasicFilters = Lazy.create(() => [...filters.values()].every((groups) => groups.length < 2));
	}

	/**
	 * The method to check whenever the TraversalData contains the filters for the basic filtering format
	 * which used to be a single dimension array of stings, but currently each filter domain has
	 * 2d array os strings, so to keep backward capability with the basic filtering we should check if the
	 * filter's domain has no more than one group of labels:
	 *
	 * ```ts
	 * [] // basic
	 *
	 * [
	 *   ["label:a", "label:b"]
	 * ] // basic
	 *
	 * [
	 *   ["label:a", "label:b"],
	 *   ["label:c", "label:d"],
	 * ] // not basic
	 * ```
	 */
	public containsBasicFilters(): boolean {
		return this.isExtractable && this.hasBasicFilters.get();
	}

	/**
	 * The method returns all filters for the domain flattened from the 2d
	 * because so far there is no need to operate with 2d shape, but there is a reason to
	 * store it in 2d shape for correct transforming to the STQL query.
	 */
	public getFlatFiltersFor(domain: string): undefined | Array<string> {
		return this.filters.get(domain)?.flat();
	}

	/**
	 * If the domain contains no more than one filters group the method returns it,
	 * otherwise the filters are not basic so `undefined` is returned.
	 */
	public getBasicFiltersFor(domain: string): undefined | Array<string> {
		if (!this.containsBasicFilters()) {
			return undefined;
		}

		return this.filters.get(domain)?.[0];
	}

	/**
	 * The method immutably replaces filters in the domain. If the second argument is an empty array, it removes the domain.
	 *
	 * Pass the second argument the second argument with a single inner group like that
	 * ```ts
	 * [
	 *   ["label:a", "label:b"]
	 * ]
	 * ````
	 *
	 * in cases when you work with basic filters.
	 *
	 * It won't be basic filters if the second argument have more than one group like that
	 * ```ts
	 * [
	 *   ["label:a", "label:b"],
	 *   ["label:c", "label:d"],
	 * ]
	 * ```
	 */
	public replaceFiltersFor(domain: string, filters: Array<Array<string>>): TraversalData {
		const validValues = filters.map(uniqNotEmpty).filter(isNotEmpty);

		return new TraversalData(
			validValues.length === 0 ? this.filters.delete(domain) : this.filters.set(domain, validValues),
			this.componentNames,
			this.clausesWithNeighbors,
			this.componentIdentifiers,
			this.isExtractable,
		);
	}

	/**
	 * Immutably replaces the `TraversalData#componentNames` field.
	 */
	public replaceComponentNames(componentNames: Array<string>): TraversalData {
		return new TraversalData(
			this.filters,
			componentNames,
			this.clausesWithNeighbors,
			this.componentIdentifiers,
			this.isExtractable,
		);
	}

	/**
	 * Immutably replaces the `TraversalData#componentIdentifiers` field.
	 */
	public replaceComponentIdentifiers(componentIdentifiers: Array<string>): TraversalData {
		return new TraversalData(
			this.filters,
			this.componentNames,
			this.clausesWithNeighbors,
			componentIdentifiers,
			this.isExtractable,
		);
	}

	/**
	 * Immutably replaces the `TraversalData#clausesWithNeighbors` field.
	 */
	public replaceClausesWithNeighbors(clausesWithNeighbors: Array<string>): TraversalData {
		return new TraversalData(
			this.filters,
			this.componentNames,
			clausesWithNeighbors,
			this.componentIdentifiers,
			this.isExtractable,
		);
	}

	/**
	 * Convert the TraversalData into an STQL query string.
	 *
	 * Filters converts to `(filter:a OR filter:b) AND (filter:c OR filter:d)` from 2D array:
	 *
	 * @example
	 * TraversalData.empty
	 *   .replaceFiltersFor("label", [
	 *     ["label:a", "label:b"],
	 *     ["label:c"],
	 *     ["label:d", "label:e"]
	 *   ])
	 *   .replaceFiltersFor("health", [["CRITICAL"]])
	 *   .toSTQL()
	 * // equals to
	 * `label IN (label:a, label:b) AND label IN (label:c) AND label IN (label:d, label:E) AND health IN (CRITICAL)`
	 */
	public toSTQL({ sortValues }: { sortValues?: (left: string, right: string) => number } = {}): string {
		const sorter = (arr: Array<string>) => {
			if (sortValues == null) {
				return arr;
			}

			return arr.slice().sort(sortValues);
		};

		const queryFilters = this.filters
			.toArray()
			.sort(([leftKey], [rightKey]) => alphabeticalSorter(leftKey, rightKey))
			.flatMap(([key, value]) => value.map((group) => queryGroup(key, sorter(group))))
			.filter(isNotEmpty);

		return [
			isNotEmpty(queryFilters) ? `(${queryFilters.join(" AND ")})` : "",
			queryGroup("name", sorter(this.componentNames)),
			sorter(this.clausesWithNeighbors).join(" OR "),
			queryGroup("identifier", sorter(this.componentIdentifiers)),
		]
			.filter(isNotEmpty)
			.join(" OR ");
	}
}

// Escape double quotes only if not already escaped
// reference https://gist.github.com/getify/3667624
export function escapeVal(val: string) {
	return val.replace(/\\([\s\S])|(")/gu, "\\$1$2");
}

export function fromNeighborsOfToString(predicates: Array<NeighborsOf>, query: string): Array<string> {
	return predicates.map(({ start, end }) => query.slice(start, end));
}

export function parseQuery(query: string): Either<ParseError, string> {
	const validation = Parser.isValid(query);
	return isParseError(validation) ? left(validation) : right(query);
}

interface GenerateNeighborsOfStringData {
	id: number | Array<NodeId> | string;
	direction: NeighborsOf["direction"];
	levels: NeighborsOf["levels"];
}
export function generateNeighborsOfString({
	id,
	direction,
	levels,
}: GenerateNeighborsOfStringData): string | undefined {
	if (typeof id === "string") {
		return `${WITH_NEIGHBORS_OF}(direction = "${direction}", components = (${id}), levels = "${levels}")`;
	}

	const normalizedId = isArray(id) ? id : [id];
	if (normalizedId.length === 0) {
		return undefined;
	}

	return `${WITH_NEIGHBORS_OF}(direction = "${direction}", components = (${generateComponentOfString(
		id,
	)}), levels = "${levels}")`;
}

export function generateComponentOfString(id: number | Array<NodeId>): string {
	const normalizedId = isArray(id) ? id : [id];
	if (normalizedId.length === 0) {
		return "";
	}
	return `id ${
		normalizedId.length > 1 ? `IN (${normalizedId.map((id) => `"${id}"`).join(", ")})` : `= "${normalizedId[0]}"`
	}`;
}

export function isParseError(result: any): result is ParseError {
	return result instanceof ParseError;
}

export function queryDisjunction(query: string, disjunction: string): string {
	return `(${query}) OR (${disjunction})`;
}

export const queryGroup = (key: string, values: Array<string>): string => {
	if (values.length === 0) {
		return "";
	}

	return `${key} IN (${values.map((val) => `"${escapeVal(val)}"`).join(", ")})`;
};
